import React, { useState, useEffect } from 'react';
import { Settings } from 'lucide-react';
import CardBasic from '../ui/Card.jsx';
import WizardStepper from './WizardStepper.jsx';
import StepBasedata from './steps/StepBasedata.jsx';
import StepLogos from './steps/StepLogos.jsx';
import StepSignature from './steps/StepSignature.jsx';
import StepNumberCircles from './steps/StepNumberCircles.jsx';
import StepComplete from './steps/StepComplete.jsx';

const WizardIndex = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [stepsCompleted, setStepsCompleted] = useState({ 1: false, 2: false, 3: false, 4: false });
    const [wizardData, setWizardData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchWizardData = async () => {
            try {
                const response = await fetch('/index.php/wizard/getWizardData');
                if (!response.ok) throw new Error('Failed to fetch wizard data');
                const data = await response.json();
                setWizardData(data);

                // Pre-check completed steps based on existing data
                const completed = { 1: false, 2: false, 3: false, 4: false };

                // Step 1: Check if required basedata fields are filled
                if (data.basedata?.surname && data.basedata?.name && data.basedata?.company && data.basedata?.email) {
                    completed[1] = true;
                }

                // Step 2: Check if logo exists
                if (data.logos?.logo?.exists) {
                    completed[2] = true;
                }

                // Step 3: Check if signature exists
                if (data.signature?.exists && data.signature?.name) {
                    completed[3] = true;
                }

                setStepsCompleted(completed);

                // Navigate to first incomplete step
                for (let i = 1; i <= 4; i++) {
                    if (!completed[i]) {
                        setCurrentStep(i);
                        break;
                    }
                    if (i === 4) setCurrentStep(5);
                }
            } catch (error) {
                console.error('Error fetching wizard data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchWizardData();
    }, []);

    const handleStepComplete = (step) => {
        setStepsCompleted(prev => ({ ...prev, [step]: true }));
        setCurrentStep(step + 1);
    };

    const handleStepClick = (step) => {
        setCurrentStep(step);
    };

    if (isLoading) {
        return (
            <div>
                <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Lädt...</span>
                    </div>
                </div>
            </div>
        );
    }

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <StepBasedata
                        initialData={wizardData?.basedata}
                        titles={wizardData?.titles}
                        onComplete={() => handleStepComplete(1)}
                    />
                );
            case 2:
                return (
                    <StepLogos
                        initialLogos={wizardData?.logos}
                        onComplete={() => handleStepComplete(2)}
                    />
                );
            case 3:
                return (
                    <StepSignature
                        initialSignature={wizardData?.signature}
                        onComplete={() => handleStepComplete(3)}
                    />
                );
            case 4:
                return (
                    <StepNumberCircles
                        initialData={wizardData?.numberCircles}
                        onComplete={() => handleStepComplete(4)}
                    />
                );
            case 5:
                return (
                    <StepComplete
                        stepsCompleted={stepsCompleted}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div>
            {/* Header */}
            <div className="mb-4">
                <h1 className="h2 fw-bold text-dark d-flex align-items-center gap-2 mb-2">
                    <Settings size={32} className="icon-wizard" style={{ color: 'var(--accent-slate, #5b7c8d)' }} />
                    Erste Einrichtung
                </h1>
                <p className="text-muted mb-0">
                    Richte dein Konto in wenigen Schritten ein
                </p>
            </div>

            {/* Wizard Card */}
            <CardBasic noPadding>
                <WizardStepper
                    currentStep={currentStep}
                    stepsCompleted={stepsCompleted}
                    onStepClick={handleStepClick}
                />
                <div style={{
                    borderTop: '1px solid var(--border-light, #e2e8f0)'
                }}>
                    {renderStep()}
                </div>
            </CardBasic>
        </div>
    );
};

export default WizardIndex;
